/**
 * Municipality Permit Requirements — Phase 1B
 * ─────────────────────────────────────────────────────────────────────
 * Jurisdiction-specific checklist of items needed inside a permit packet
 * before submission. Resolver returns a flat list of canonical items
 * (filtered by `appliesWhen`) for a given municipality + permit type.
 *
 * Scope (locked to Phase 1B):
 *  - Seeded municipality profiles for Hillsborough County, City of Tampa,
 *    Pinellas County. Everything else falls back to GENERIC_PROFILE.
 *  - `appliesWhen` is a pure predicate over `{ permitType }`. Wrapped in
 *    try/catch at the resolver — a broken predicate degrades to "always
 *    applies" rather than crashing.
 *  - Items only declare *what is required*. Completion state lives on
 *    `Job.permitDetail.requirementChecklist` and is mutated through
 *    `requirementsChecklist.ts`.
 *
 * Out of scope (do not add here):
 *  - revision intake, inspection tracker, attachments, automation,
 *    customer permit visibility, hard submission gating.
 */
import type { MunicipalityId } from "@/data/floridaMunicipalities";
import { getMunicipalityId } from "@/data/floridaMunicipalities";

export type RequirementCategory =
  | "documents"
  | "drawings"
  | "approvals"
  | "fees"
  | "owner_info";

export interface RequirementItem {
  /** Stable key — used in the per-job checklist ledger. Must be unique within a profile. */
  key: string;
  label: string;
  category: RequirementCategory;
  /** One-line operator-facing helper text. */
  hint?: string;
  /**
   * "file" (default) — shows the per-category FileUploader.
   * "link" — shows a URL input and "Open" button instead of a file uploader.
   */
  type?: "file" | "link";
  /** Predicate over the permit context. Omitted = always applies. */
  appliesWhen?: (ctx: { permitType: string; hasSecondBathroom?: boolean }) => boolean;
}

export interface RequirementProfile {
  municipalityId: MunicipalityId | "generic";
  label: string;
  items: RequirementItem[];
}

// ─── Generic baseline (always available) ───────────────────────────────
const GENERIC_PROFILE: RequirementProfile = {
  municipalityId: "generic",
  label: "Generic permit packet",
  items: [
    { key: "site_plan",          category: "drawings",   label: "Site plan", hint: "Scaled site drawing showing scope location." },
    { key: "scope_narrative",    category: "documents",  label: "Scope of work narrative" },
    { key: "owner_auth",         category: "owner_info", label: "Signed owner authorization" },
    { key: "contractor_lic",     category: "documents",  label: "Contractor license on file" },
    { key: "noc",                category: "documents",  label: "Notice of Commencement (if > $5k)", appliesWhen: () => true },
    { key: "fee_estimate",       category: "fees",       label: "Permit fee estimate confirmed" },
    {
      key: "floor_plan_primary",
      category: "drawings",
      label: "Completed Drawing / Floor Plan",
      hint: "Full-scale floor plan drawing for primary bathroom scope.",
      appliesWhen: ({ permitType }) => /bathroom|shower|bath/i.test(permitType),
    },
    {
      key: "floor_plan_second_bath",
      category: "drawings",
      label: "Completed Drawing / Floor Plan (Second Bathroom)",
      hint: "Full-scale floor plan drawing for second bathroom scope.",
      appliesWhen: ({ permitType, hasSecondBathroom }) =>
        /bathroom|shower|bath/i.test(permitType) && !!hasSecondBathroom,
    },
    {
      key: "online_portal_link",
      category: "fees",
      label: "Online Permit Portal",
      hint: "Link to the municipality's permit submission portal.",
      type: "link",
    },
  ],
};

// ─── Seeded jurisdiction profiles ──────────────────────────────────────
const HILLSBOROUGH: RequirementProfile = {
  municipalityId: "hillsborough-county",
  label: "Hillsborough County Building Services",
  items: [
    ...GENERIC_PROFILE.items,
    { key: "wind_calcs",      category: "drawings",   label: "Wind load calculations (FL product approval)", hint: "Required for any exterior envelope work." },
    { key: "energy_calcs",    category: "documents",  label: "Energy calcs (Form R405 / R402)",
      appliesWhen: ({ permitType }) => /window|door|reroof|insulation|envelope/i.test(permitType) },
    { key: "hoa_letter",      category: "approvals",  label: "HOA approval letter (if applicable)" },
    { key: "flood_zone",      category: "owner_info", label: "Flood zone determination" },
  ],
};

const CITY_OF_TAMPA: RequirementProfile = {
  municipalityId: "city-of-tampa",
  label: "City of Tampa Construction Services",
  items: [
    ...GENERIC_PROFILE.items,
    { key: "wind_calcs",      category: "drawings",   label: "Wind load calculations (FL product approval)" },
    { key: "historic_review", category: "approvals",  label: "Historic district review (if in district)" },
    { key: "tree_survey",     category: "documents",  label: "Tree / grand-tree survey",
      appliesWhen: ({ permitType }) => /addition|new construction|deck|patio/i.test(permitType) },
  ],
};

const PINELLAS: RequirementProfile = {
  municipalityId: "pinellas-county",
  label: "Pinellas County Building Services",
  items: [
    ...GENERIC_PROFILE.items,
    { key: "wind_calcs",      category: "drawings",   label: "Wind load calculations (FL product approval)" },
    { key: "flood_elev",      category: "documents",  label: "FEMA elevation certificate (coastal/flood zone)" },
    { key: "seawall_clear",   category: "approvals",  label: "Seawall clearance (if waterfront)" },
  ],
};

const PROFILES: Partial<Record<MunicipalityId, RequirementProfile>> = {
  "hillsborough-county": HILLSBOROUGH,
  "city-of-tampa": CITY_OF_TAMPA,
  "pinellas-county": PINELLAS,
};

/**
 * Resolve the requirement profile for a municipality. Falls back to the
 * generic profile when the municipality is unknown, "unmapped", or not
 * yet seeded.
 */
export function getRequirementProfile(
  municipalityIdOrLabel: MunicipalityId | string | null | undefined,
): RequirementProfile {
  if (!municipalityIdOrLabel) return GENERIC_PROFILE;
  // Accept both canonical IDs and display strings.
  const id =
    PROFILES[municipalityIdOrLabel as MunicipalityId]
      ? (municipalityIdOrLabel as MunicipalityId)
      : getMunicipalityId(municipalityIdOrLabel as string);
  if (!id || id === "unmapped") return GENERIC_PROFILE;
  return PROFILES[id] ?? GENERIC_PROFILE;
}

/**
 * Resolve the *applicable* required items for a permit context. A broken
 * predicate degrades to "applies" — coordination should never be silently
 * blocked by a typo in `appliesWhen`.
 */
export function getRequiredPermitItems(
  municipalityIdOrLabel: MunicipalityId | string | null | undefined,
  ctx: { permitType: string; hasSecondBathroom?: boolean },
): RequirementItem[] {
  const profile = getRequirementProfile(municipalityIdOrLabel);
  return profile.items.filter((item) => {
    if (!item.appliesWhen) return true;
    try {
      return item.appliesWhen(ctx);
    } catch {
      return true;
    }
  });
}

export const REQUIREMENT_CATEGORY_LABEL: Record<RequirementCategory, string> = {
  documents: "Documents",
  drawings: "Drawings",
  approvals: "Approvals",
  fees: "Fees",
  owner_info: "Owner Info",
};
